import React, { useEffect, useMemo, useState } from 'react';
import { Button, ColumnVisibilityMenu, DataTable, SearchInput, useDataTable } from '@onward-delivery/design';
import { parentColumns } from './columns';
import {
    useFacetOptions,
    useFacetRollup,
    useForwardStockingSkus,
    useForwardStockingSummary,
    useItemSearchMatches,
} from './hooks';
import { useClientUser } from '@/hooks';
import { facetFor } from './constants';
import { applyFacetRollup, filterSkuRows } from './utils';
import FacetFilter from './FacetFilter';
import SkuItemsTable from './SkuItemsTable';
import StatCards from './StatCards';
import ReturnsOverview from './ReturnsOverview';
import CreateOrderModal from './CreateOrderModal';

const ForwardStocking = () => {
    const { skus, loading, error } = useForwardStockingSkus();
    const { summary, loading: summaryLoading, error: summaryError } = useForwardStockingSummary();
    const { accountType } = useClientUser();
    const columns = useMemo(() => parentColumns(accountType), [accountType]);
    const [createOpen, setCreateOpen] = useState(false);

    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState({});
    const { matchedSkus, matchedItemIds } = useItemSearchMatches(search);

    const facet = useMemo(() => facetFor(accountType), [accountType]);
    const [selectedFacetIds, setSelectedFacetIds] = useState([]);
    const { options: facetChoices, loading: facetChoicesLoading } = useFacetOptions(facet);
    const { rollup, loading: rollupLoading } = useFacetRollup(facet, selectedFacetIds);
    const rollupPending = rollupLoading && !rollup;

    // The filter is a hard scope on which inventory is in play: it narrows and
    // re-counts the SKU rows and KPI cards, and the search looks inside it.
    const facetedSkus = useMemo(() => applyFacetRollup(skus, rollup?.bySku), [skus, rollup]);
    const facetedSummary = rollup?.summary ?? (rollupPending ? null : summary);

    // Filtering happens here rather than through the table's own global filter:
    // that one only sees the rows in the browser, and a serial number match is
    // something only the server can answer.
    const { rows: visibleSkus, itemMatchedSkus } = useMemo(
        () => filterSkuRows(facetedSkus, search, matchedSkus, { includeShipper: accountType === 'carrier' }),
        [facetedSkus, search, matchedSkus, accountType]
    );

    // Rows that only qualified because one of their items matched open
    // themselves — landing on the item is the point of searching for it. Keyed
    // on the set's contents so this fires when the match changes and not on
    // every render, which would re-open a row the user just collapsed.
    //
    // Merged into the previous state, not assigned over it: a row the user
    // opened by hand is still on screen, and replacing the map collapsed it as
    // soon as a serial match landed on some other SKU.
    const itemMatchKey = useMemo(() => Array.from(itemMatchedSkus).sort().join('|'), [itemMatchedSkus]);
    useEffect(() => {
        if (!itemMatchKey) {
            return;
        }
        setExpanded((prev) => ({
            ...prev,
            ...Object.fromEntries(itemMatchKey.split('|').map((sku) => [sku, true])),
        }));
    }, [itemMatchKey]);

    // One instance shared with the toolbar, so its search box and the table read
    // the same state rather than each owning a copy.
    const table = useDataTable({
        columns,
        data: visibleSkus,
        getRowId: (row) => row.sku,
        expanded,
        onExpandedChange: setExpanded,
        renderSubTable: (row) => (
            <SkuItemsTable
                row={row}
                accountType={accountType}
                // Null means "no item filter": either nothing is being searched
                // at item level, or this row earned its place on its own text.
                itemIds={itemMatchedSkus.has(row.sku) ? matchedItemIds : null}
                // Kept separate from itemIds rather than merged into it: they
                // are independent reasons to narrow, and a row can be subject
                // to one, both, or neither.
                facet={facet}
                selectedFacetIds={selectedFacetIds}
            />
        ),
    });

    return (
        <div className="onward-design" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Only the table scrolls. Everything above it is a fixed-height
                header, so the counts and the search stay put no matter how many
                SKUs there are or how many rows are expanded. */}
            <div className="flex min-h-0 flex-1 flex-col">
                {/* Leads the page, above the stat cards, as in the design: it is
                    the page's primary action, not a footnote to the table. */}
                {accountType === 'shipper' ? (
                    <div className="flex shrink-0 pb-4">
                        <Button onClick={() => setCreateOpen(true)}>Create order</Button>
                    </div>
                ) : null}
                {accountType === 'shipper' ? <CreateOrderModal open={createOpen} onOpenChange={setCreateOpen} /> : null}

                <StatCards
                    summary={facetedSummary}
                    skus={rollupPending ? null : facetedSkus}
                    loading={summaryLoading || rollupPending}
                    skusLoading={loading || rollupPending}
                    error={error || summaryError}
                />
                <ReturnsOverview
                    summary={facetedSummary}
                    loading={summaryLoading || rollupPending}
                    error={error || summaryError}
                />

                {error ? (
                    <div className="px-3 py-2 text-sm">Forward stocked inventory could not be loaded.</div>
                ) : (
                    <>
                        {/* Composed rather than DataTableToolbar: its search box
                            can only filter rows already in the browser, and
                            turning it off leaves a flex spacer that pushes ours
                            off the left edge. The pieces are the same ones it
                            would render. It is chrome, not a table, so it gets
                            its own surface and reads as one bar. */}
                        <div className="mb-4 flex shrink-0 flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
                            <SearchInput
                                className="min-w-48 flex-1"
                                placeholder="Search SKU, item, serial, or line item…"
                                value={search}
                                onValueChange={setSearch}
                            />
                            <FacetFilter
                                facet={facet}
                                options={facetChoices}
                                selectedIds={selectedFacetIds}
                                onChange={setSelectedFacetIds}
                                loading={facetChoicesLoading}
                            />
                            <span className="shrink-0 text-sm text-muted">
                                {table.getFilteredRowModel().rows.length.toLocaleString()} SKUs
                            </span>
                            <ColumnVisibilityMenu table={table} />
                        </div>
                        {/* The surface becomes the flex column so its own scroll
                            container (the inner div) is the thing that shrinks
                            and scrolls, keeping the border and rounded corners
                            still while the rows move inside them.

                            The header is pinned by selector because DataTable
                            only sticks it for virtualized tables and this one is
                            not — a bounded table whose header scrolls away is
                            worse than an unbounded one. The child path is exact
                            (surface > scroll > table > thead) so an expanded
                            row's own table header is left alone. A stickyHeader
                            prop on DataTable would be the better home for this. */}
                        <DataTable
                            table={table}
                            isLoading={loading}
                            emptyState="No forward stocked inventory"
                            className="flex min-h-0 flex-1 flex-col [&>div>table>thead]:sticky [&>div>table>thead]:top-0 [&>div>table>thead]:z-10"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default ForwardStocking;
